import React, { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert } from '@mui/material';
import {
  WAuthLayout,
  WTranslationProvider,
  WResetPasswordForm,
} from '@wareclouds/wareclouds-ui';

import logo from '../../../assets/img/logo.svg';
import { useYupValidationTranslations } from '../../common/hooks';
import {
  useEditPassword,
  useEncryptPassword,
  useVerifyCustomToken,
} from '../services';
import { passwordResetSchema, defaultValuesPasswordResetForm } from '../utils';

export default function PasswordResetPage() {
  const { t } = useTranslation(['auth']);

  const [token, setToken] = useState('');

  const location = useLocation();
  const navigate = useNavigate();

  const translateError = useYupValidationTranslations();

  const {
    mutate: encryptPassword,
    isPending: isEncryptPending,
    isError: isEncryptError,
  } = useEncryptPassword({ token });

  const {
    mutate: editPassword,
    isPending: isEditPending,
    isError: isEditError,
  } = useEditPassword();

  const { mutate: validateToken } = useVerifyCustomToken();

  const onSubmit = (data) => {
    encryptPassword(
      { password: data.password, token },
      {
        onSuccess: (encryptedPasswordData) => {
          const encryptedPassword = encryptedPasswordData?.data;
          if (!encryptedPassword) return;
          handleEditPassword({ password: encryptedPassword, token });
        },
      }
    );
  };

  const handleEditPassword = (data) => {
    editPassword(
      { password: data.password, token },
      {
        onSuccess: () => {
          navigate('/reset-password-success', { replace: true });
        },
      }
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramToken = params.get('token');
    const locationToken = location.state?.token;

    const handleRedirect = () => {
      navigate('/not-found', { replace: true });
    };

    const handleValidateToken = (token) => {
      validateToken(
        { token },
        {
          onSuccess: () => {
            setToken(token);
          },
          onError: () => {
            handleRedirect();
          },
        }
      );
    };

    if (paramToken) {
      handleValidateToken(paramToken);
    } else if (locationToken) {
      setToken(locationToken);
    } else {
      handleRedirect();
    }
  }, [navigate, location, validateToken]);

  return (
    <WTranslationProvider t={t} translateError={translateError}>
      <WAuthLayout
        title={t('password_reset.title', { ns: 'auth' })}
        subtitle={t('password_reset.subtitle', { ns: 'auth' })}
        logo={logo}
      >
        <WResetPasswordForm
          onSubmit={onSubmit}
          onReturnClick={() => {
            navigate('/login');
          }}
          isSubmitting={isEncryptPending || isEditPending}
          resolver={yupResolver(passwordResetSchema)}
          defaultValues={defaultValuesPasswordResetForm}
        />
        {(isEncryptError || isEditError) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('password_reset.error', { ns: 'auth' })}
          </Alert>
        )}
      </WAuthLayout>
    </WTranslationProvider>
  );
}
